import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { flattenedDataToEnrichedCanonical } from 'services/datachecks/flattenedDataToEnrichedCanonical';
import { queryClient } from 'services/http/queryClient';
import { logError } from 'services/logging/logging';
import { useMessageWithProcessSpecification, useUpdateMessage } from 'services/repositories/messages/messages';
import { useCurrentPartner } from 'services/repositories/partners/partners';
import { logSentWithHiddenErrors } from 'services/webediErrors/hiddenErrorsSubmission';
import { addNotification, NotificationType } from 'stores/notifications/notifications';
import { ConflictError } from 'support/helpers/errors/errors';
import { DocumentType, MessageV2DTO, ProcessSpecificationDTO, TransactionInternalType } from 'support/types';

import { fetchAppliedWorkflows } from './helpers';

type UseEditMessageParams = {
  message: MessageV2DTO | undefined;
  processSpecificationFields: ProcessSpecificationDTO['fields'] | undefined;
  isAdmin?: boolean;
  onSuccess?: () => void;
  withAIAgents?: boolean;
  skipQueryReset?: boolean;
};

export function useEditMessage({
  message,
  processSpecificationFields,
  isAdmin,
  onSuccess,
  withAIAgents,
  skipQueryReset,
}: UseEditMessageParams) {
  const { data: currentPartner } = useCurrentPartner();
  const { mutate: mutateUpdateMessage, isSuccess, error } = useUpdateMessage();
  const { t } = useTranslation();

  const resetMessageQuery = useCallback(() => {
    if (message?.id) {
      queryClient.resetQueries({ queryKey: useMessageWithProcessSpecification.getKey({ messageId: message.id }) });
    }
  }, [message?.id]);

  const [isEditingMessage, setIsEditingMessage] = useState(false);
  const editMessage = useCallback(
    async (formValues: any, annotationIds: Array<string> = []) => {
      if (isEditingMessage) {
        logError('Already editing message');
        return;
      }
      if (!message) {
        logError('No message found to edit');
        return;
      }
      setIsEditingMessage(true);
      if (!processSpecificationFields) {
        logError('No process specification found');
        setIsEditingMessage(false);
        return;
      }

      try {
        const enrichedCanonical = await flattenedDataToEnrichedCanonical(
          processSpecificationFields,
          message.type as DocumentType,
          formValues,
        );
        const usedAgents =
          currentPartner?.id && withAIAgents
            ? await fetchAppliedWorkflows(message.id, currentPartner.id, isAdmin ?? false)
            : [];

        mutateUpdateMessage(
          {
            messageId: message.id,
            data: {
              data: enrichedCanonical,
              message_update_key: message.updated_at,
              ...(usedAgents.length > 0 ? { used_agents: usedAgents } : {}),
              ...(annotationIds.length > 0 ? { annotation_ids: annotationIds } : {}),
            },
          },
          {
            onSuccess: () => {
              logSentWithHiddenErrors({ id: message.id, type: TransactionInternalType.message });
              if (!skipQueryReset) {
                resetMessageQuery();
              }
              onSuccess?.();
            },
            onError: (err) => {
              logError(err);
              if (err instanceof ConflictError) {
                addNotification(
                  {
                    title: t('webedi:editDocument.failedToast.title'),
                    subtitle: t('webedi:editDocument.failedToast.subtitle'),
                  },
                  NotificationType.error,
                );
              } else {
                addNotification(
                  {
                    title: t('webedi:editDocument.failedToastGeneric.title'),
                  },
                  NotificationType.error,
                );
              }
            },
            onSettled: () => {
              setIsEditingMessage(false);
            },
          },
        );
      } catch (error) {
        logError(error);
        addNotification(
          {
            title: t('common:error.generic'),
          },
          NotificationType.error,
        );
        setIsEditingMessage(false);
      }
    },
    [
      isEditingMessage,
      message,
      processSpecificationFields,
      currentPartner,
      withAIAgents,
      isAdmin,
      mutateUpdateMessage,
      skipQueryReset,
      onSuccess,
      resetMessageQuery,
      t,
    ],
  );

  return { editMessage, isUpdating: isEditingMessage, isSuccess, error, resetMessageQuery };
}
